import { useState } from 'react'
import { httpService } from '../../services/http-service'
import { GlueTriggerTable, groupTriggers } from '../../cmps/glue-trigger/glue-trigger-table'
import { GlueTriggerFormModal } from '../../cmps/glue-trigger/glue-trigger-form-modal'
import { Spinner } from '../../cmps/util-cmps/spinner'
import { useCachedFetch } from '../../hooks/use-cached-fetch'

export const GlueTriggersPage: React.FC = () => {
    const { data, isLoading, hasError, refetch } = useCachedFetch(
        'glue-triggers',
        async () => {
            const result = await httpService.get('/glue-schedules')
            return result.schedules as any[]
        }
    )

    const [isModalOpen, setIsModalOpen] = useState(false)
    const [triggerToEdit, setTriggerToEdit] = useState<any>(null)

    const grouped = groupTriggers(data || [])
    const inactiveSchedules = grouped.filter((t: any) => t.state !== 'ACTIVATED')

    const openCreateModal = () => {
        setTriggerToEdit(null)
        setIsModalOpen(true)
    }

    const openEditModal = (trigger: any) => {
        setTriggerToEdit(trigger)
        setIsModalOpen(true)
    }

    const closeModal = () => {
        setIsModalOpen(false)
        setTriggerToEdit(null)
    }

    const handleSuccess = () => {
        closeModal()
        refetch()
    }

    return <div className="tablePageContainer">
        <div className="page-header">
            <p className="tableSubtitle">View and manage ETL schedules.</p>
            <div className="page-header-actions">
                <button className="refresh-btn" onClick={refetch} disabled={isLoading} title="Refresh">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="23 4 23 10 17 10" />
                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                    </svg>
                </button>
                <button className="add-btn" onClick={openCreateModal}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    Create Schedule
                </button>
            </div>
        </div>

        {!isLoading && inactiveSchedules.length > 0 && (
            <div className="inactive-triggers-warning">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <span>
                    <strong>{inactiveSchedules.length} schedule{inactiveSchedules.length > 1 ? 's are' : ' is'} not activated.</strong>
                    {' '}Inactive schedules will not run.
                </span>
            </div>
        )}

        {isLoading && <Spinner />}
        {!isLoading && !hasError && <GlueTriggerTable data={data || []} onEditClick={openEditModal} />}
        {!isLoading && hasError && <div className="error-hint">Failed to load ETL schedules. Try refreshing the page or re-logging in.</div>}

        {isModalOpen && <GlueTriggerFormModal
            key={triggerToEdit?.trigger_name || 'create'}
            triggerToEdit={triggerToEdit}
            onSuccess={handleSuccess}
            onClose={closeModal}
        />}
    </div>
}
